import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'create-account', title: 'Creating your SweetPilot account' },
  { id: 'sign-in-existing', title: 'Sign in to an existing account' },
  { id: 'google-sign-in', title: 'Sign in with Google' },
  { id: 'troubleshooting', title: 'Troubleshooting login issues' },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'Plans and pricing', href: '/doc/article/pricing-and-plan' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Account & billing settings', href: '/doc/collections/account-billing' },
  { title: 'Privacy & security', href: '/doc/collections/privacy-security' },
];

export default function LoginToSweetPilot() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Login to SweetPilot - Account Access Guide',
    description: 'Learn how to create your SweetPilot account, sign in to an existing account, use Google sign-in, and troubleshoot common login issues.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/login-to-sweetpilot' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Login to SweetPilot - Create Account & Sign In Guide | SweetPilot Help Center</title>
        <meta name="description" content="Learn how to create your SweetPilot account, sign in with email or Google, and troubleshoot common login issues. Step-by-step account setup guide." />
        <meta name="keywords" content="SweetPilot login, SweetPilot sign in, create SweetPilot account, SweetPilot registration, Google sign in SweetPilot, SweetPilot account setup" />
        <meta property="og:title" content="Login to SweetPilot - Account Access Guide" />
        <meta property="og:description" content="Step-by-step guide to create your SweetPilot account and sign in. Includes Google sign-in and troubleshooting tips." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/login-to-sweetpilot" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Login to SweetPilot - Account Guide" />
        <meta name="twitter:description" content="Create your SweetPilot account and sign in with email or Google." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/login-to-sweetpilot" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/docs"
                    className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                  >
                    All Collections
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li>
                  <Link
                    to="/doc/collections/getting-started"
                    className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                  >
                    Getting started
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Login to SweetPilot
                </li>
              </ol>
            </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Login to SweetPilot
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn how to create your SweetPilot account, sign in to an existing account, and troubleshoot login issues.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              In this article, we'll cover what you need to know when creating a new SweetPilot account, how to sign in to an existing account, and how to troubleshoot the login issues you may have.
            </p>

            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              To use SweetPilot, <strong>you first need to create an account</strong>. If you don't have an account yet, you'll be redirected to our registration page.{' '}
              <Link
                to="/pricing"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Learn about our plans
              </Link>{' '}
              and get a subscription according to your needs on{' '}
              <Link
                to="/pricing"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                SweetPilot's pricing page
              </Link>.
            </p>

            {/* Creating your SweetPilot account */}
            <h2 id="create-account" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Creating your SweetPilot account
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              If it's your first time using SweetPilot, you'll need to create an account. Follow the steps below:
            </p>

            <ol className={`list-decimal pl-6 mb-6 space-y-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                Go to{' '}
                <Link
                  to="/login"
                  className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  SweetPilot's login page
                </Link>{' '}
                and <strong>enter your email</strong>:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step1-fullname.png"
                    alt="SweetPilot registration page - Full Name field"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
              <li>
                <strong>Enter your email</strong> address that you want to associate with your SweetPilot account:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step2-email.png"
                    alt="SweetPilot registration page - Email field highlighted"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
              <li>
                <strong>Create a secure password</strong>. Your password must meet the following requirements:
                <ul className={`list-disc pl-6 mt-2 mb-4 space-y-1 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  <li>At least <strong>8+ characters</strong></li>
                  <li>Contains a <strong>lowercase letter</strong></li>
                  <li>Contains an <strong>uppercase letter</strong></li>
                  <li>Contains a <strong>number</strong></li>
                </ul>
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step3-password.png"
                    alt="SweetPilot registration page - Password field with strength indicators"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
              <li>
                Click <strong>"Create Account"</strong> to complete your registration:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step4-create-account.png"
                    alt="SweetPilot registration page - Create Account button"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
            </ol>

            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Once your account is created, you'll be redirected to your SweetPilot dashboard where you can start using AI employees and exploring the platform. You'll receive <strong>100 free credits</strong> to get started!
            </p>

            {/* Sign in to an existing account */}
            <h2 id="sign-in-existing" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sign in to an existing account
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              If you already have a SweetPilot account, you can sign in by following these steps:
            </p>

            <ol className={`list-decimal pl-6 mb-6 space-y-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                Go to{' '}
                <Link
                  to="/login"
                  className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  SweetPilot's login page
                </Link>{' '}
                or click <strong>"Sign in"</strong> on the registration page:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step5-signin-link.png"
                    alt="SweetPilot registration page - Sign in link"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
              <li>
                <strong>Enter your email</strong> address associated with your SweetPilot account.
              </li>
              <li>
                <strong>Enter your password</strong> and click <strong>"Sign in"</strong>.
              </li>
            </ol>

            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Now you can access your SweetPilot dashboard and continue where you left off!
            </p>

            {/* Sign in with Google */}
            <h2 id="google-sign-in" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sign in with Google
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              You can also create an account or sign in using your Google account for a faster, passwordless experience:
            </p>

            <ol className={`list-decimal pl-6 mb-6 space-y-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>
                On the login or registration page, click the <strong>"Google"</strong> button:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step6-google-option.png"
                    alt="SweetPilot - Google sign in option"
                    className="w-full max-w-md mx-auto block"
                  />
                </div>
              </li>
              <li>
                <strong>Choose your Google account</strong> from the popup window:
                <div className={`mt-4 mb-4 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                  <img
                    src="/docs/login/step7-google-account.png"
                    alt="Google account selection"
                    className="w-full max-w-lg mx-auto block"
                  />
                </div>
              </li>
              <li>
                You'll be automatically signed in and redirected to your SweetPilot dashboard.
              </li>
            </ol>

            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <strong>Note:</strong> If you sign up with Google, you can later set a password in your{' '}
              <Link
                to="/dashboard/settings"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                account settings
              </Link>{' '}
              if you prefer to have both options available.
            </p>

            {/* Troubleshooting login issues */}
            <h2 id="troubleshooting" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Troubleshooting login issues
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              If you're having trouble logging in, here are some common solutions:
            </p>

            <div className="space-y-6 mb-8">
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Forgot your password?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Click the <strong>"Forgot password?"</strong> link on the login page. Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Email not found?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Make sure you're using the same email address you used when creating your account. If you signed up with Google, try using the <strong>Google sign-in</strong> option instead.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Account locked?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  After multiple failed login attempts, your account may be temporarily locked for security. Wait a few minutes and try again, or{' '}
                  <Link
                    to="/contact"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    contact our support team
                  </Link>{' '}
                  for assistance.
                </p>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Still having issues?
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Check out our{' '}
                  <Link
                    to="/doc/collections/troubleshooting-faq"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    troubleshooting & FAQ section
                  </Link>{' '}
                  or{' '}
                  <Link
                    to="/help"
                    className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    contact support
                  </Link>.
                </p>
              </div>
            </div>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              Login to SweetPilot
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🔐</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
